import { useState, useMemo } from 'react';
import { Button, Grid, FormControl } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Form, Field } from 'react-final-form';

import PageLayout from '../../../common/layouts/pageLayout';
import FormHeaderMedium from '../../../common/components/forms/formHeaderMedium';
import SelectStyled from '../../../common/components/input/selectStyled';
import MultiSelectStyled, { SimpleMultiSelectOption } from '../../../common/components/input/multiSelectStyled';
import LoadingIndicator from '../../../common/components/loadingIndicator';
import QCXFinalDatePickerField from '../../../../shared-components/final-date-picker-field/QCXFinalDatePickerField';
import { useGetRequest, QueriesTypes } from '../../../common/hooks/queries';

import { generateDraftDachserSpreadsheet } from './relatorioDraftDanfeDachserPage.helpers';

const RelatorioDraftDanfeDachserPage = () => {
    const [processo, setProcesso] = useState<string | undefined>(undefined);
    const [inicioVigencia, setInicioVigencia] = useState<string | null>(null);
    const [fimVigencia, setFimVigencia] = useState<string | null>(null);
    const [selectedClients, setSelectedClients] = useState<any[]>([]);

    const {
        data: relatorioData,
        isError: isRelatorioError,
    } = useGetRequest(
        QueriesTypes.RelatorioLa,
        undefined,
        `processo=${processo}&inicio=${inicioVigencia}&fim=${fimVigencia}&clientes=${selectedClients.map(c => c.id).join(',')}`
    );

    const { data: danfeData } = useGetRequest(QueriesTypes.Danfe);
    const { data: clientes = [] } = useGetRequest(QueriesTypes.Clientes);

    const processos = useMemo(() => {
        if (!danfeData || typeof danfeData !== 'object') return [];
        const filtered = danfeData.filter((d: any) => d.tipoDanfe === 'ENTRADA');
        const procs: string[] = filtered.map((d: any) => d.di.atributosAdicionais.processo);
        return procs.filter((p, i) => procs.indexOf(p) === i);
    }, [danfeData]);

    const handleClientsChange = (
        _e: React.SyntheticEvent<Element, Event>,
        value: SimpleMultiSelectOption[] | null
    ) => setSelectedClients(value || []);

    const handleSubmit = () => {
        if (!relatorioData) return;

        const inicioData = inicioVigencia ? new Date(inicioVigencia) : null;
        const fimData = fimVigencia ? new Date(fimVigencia) : null;

        const inicioVigenciaDate = inicioData ? inicioData.toISOString().slice(0, 10) : null;
        const fimVigenciaDate = fimData ? fimData.toISOString().slice(0, 10) : null;

        // Converte apenas o que for necessário (mantendo compatibilidade com seu fluxo atual)
        relatorioData.forEach((item: any) => {
            if (item.despesa !== null && item.despesa !== undefined) {
                item.despesa = Number(item.despesa);
            }
        });

        generateDraftDachserSpreadsheet(
            relatorioData,
            processo,
            selectedClients,
            inicioVigenciaDate,
            fimVigenciaDate
        );
    };

    return (
        <PageLayout
            title="Planilha LA Nova"
            icon={<InsertDriveFileIcon color="secondary" />}
        >
            {false ? (
                <LoadingIndicator message="Carregando Processos e Clientes" />
            ) : (
                <Form onSubmit={handleSubmit} render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <FormHeaderMedium>
                                Aguarde o botão de gerar ficar amarelo! Filtros são opcionais.
                            </FormHeaderMedium>

                            <Grid item sm={6} md={5} lg={3}>
                                <SelectStyled
                                    options={processos.map((proc, index) => ({ id: index, value: proc }))}
                                    label="Processo"
                                    required={false}
                                    onChangeAction={(_e, newValue) => setProcesso(newValue?.value)}
                                />
                            </Grid>

                            <Grid item xs={12} sm={4} md={2}>
                                <Field name="inicioVigencia" render={({ input }) => (
                                    <QCXFinalDatePickerField
                                        required={false}
                                        validate={false}
                                        id="date-picker-inicio"
                                        key="date-picker-inicio"
                                        label="Início da vigência"
                                        format="DD/MM/YYYY"
                                        placeholder="DD/MM/YYYY"
                                        {...input}
                                        onChange={(date: string | null) => { setInicioVigencia(date); input.onChange(date); }}
                                    />
                                )} />
                            </Grid>

                            <Grid item xs={12} sm={4} md={2}>
                                <Field name="fimVigencia" render={({ input }) => (
                                    <QCXFinalDatePickerField
                                        required={false}
                                        validate={false}
                                        id="date-picker-fim"
                                        key="date-picker-fim"
                                        label="Fim da vigência"
                                        format="DD/MM/YYYY"
                                        placeholder="DD/MM/YYYY"
                                        {...input}
                                        onChange={(date: string | null) => { setFimVigencia(date); input.onChange(date); }}
                                    />
                                )} />
                            </Grid>

                            <Grid item sm={12} style={{ margin: '25px 0 0 0' }}>
                                <FormControl fullWidth>
                                    <MultiSelectStyled
                                        options={clientes.map((c: any) => ({
                                            id: c.id?.toString(),
                                            value: `(${c.pessoa.cnpj}) ${c.pessoa.nome}`,
                                        }))}
                                        onChangeAction={handleClientsChange}
                                        label="Clientes"
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item sm={2} textAlign="right">
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    type="submit"
                                    fullWidth
                                    disabled={isRelatorioError || !relatorioData}
                                >
                                    Gerar Relatório
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                )} />
            )}
        </PageLayout>
    );
};

export default RelatorioDraftDanfeDachserPage;
